import { formatDateNumber } from '@seen/core/dates'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { CONDITION_CLINICAL_STATUSES } from '@seen/core/models/conditions/constants'
import { Diagnosis } from '@seen/core/models/conditions/Diagnosis'
import { Badge } from '@seen/ui'
import { orderBy } from 'lodash-es'
import { type JSX, type ReactNode, useMemo } from 'react'
import { ActivityComment } from '#src/components/activity/ActivityComment.tsx'
import { ActivityEntry } from '#src/components/activity/ActivityEntry.tsx'
import { useVersionOriginTask } from '#src/components/activity/useVersionOriginTask.ts'
import { useResourceVersions } from '#src/hooks/useResourceVersions.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import {
  buildConditionActivity,
  type ConditionActivityEvent,
} from '#src/pages/participants/conditions/buildConditionActivity.ts'

export function ConditionActivity({
  condition,
}: {
  condition: Diagnosis
}): JSX.Element {
  const { data: comments = [] } = useSearch(
    CommentCommunication,
    CommentCommunication.searchQuery([condition.reference])
  )

  const { data: versions = [] } = useResourceVersions(Diagnosis, condition.id)
  const conditionOriginOf = useVersionOriginTask(versions)

  const conditionActivity = useMemo(
    () => buildConditionActivity(versions),
    [versions]
  )

  const all = orderBy(
    [...conditionActivity, ...comments],
    (a) =>
      a instanceof CommentCommunication ? a.sent : a.diagnosis.lastUpdated,
    ['desc']
  )

  return (
    <>
      {all.map((entry) => {
        if (entry instanceof CommentCommunication) {
          return (
            <ActivityComment key={entry.reference.reference} comment={entry} />
          )
        }
        return (
          <ActivityEntry
            key={entry.key}
            author={entry.diagnosis.lastUpdatedBy}
            time={entry.diagnosis.lastUpdated}
            action={renderConditionActivityAction(entry)}
            originTask={conditionOriginOf(entry.diagnosis)}
          />
        )
      })}
    </>
  )
}

function renderConditionActivityAction(
  entry: ConditionActivityEvent
): ReactNode {
  switch (entry.kind) {
    case 'created':
      return 'created the condition'
    case 'removed':
      return 'removed the condition'
    case 'changed-name':
      return (
        <>
          changed condition from{' '}
          <ConditionActivityConditionValue
            name={entry.previousName}
            icd10Codes={entry.previousIcd10Codes}
          />{' '}
          to{' '}
          <ConditionActivityConditionValue
            name={entry.currentName}
            icd10Codes={entry.currentIcd10Codes}
          />
        </>
      )
    case 'changed-status':
      return (
        <>
          changed status from{' '}
          <ActivityValue>
            {CONDITION_CLINICAL_STATUSES[entry.previousStatus].display}
          </ActivityValue>{' '}
          to{' '}
          <ActivityValue>
            {CONDITION_CLINICAL_STATUSES[entry.currentStatus].display}
          </ActivityValue>
        </>
      )
    case 'changed-onset-date':
      return renderConditionOnsetDateAction(entry)
    default:
      return assertNever(entry)
  }
}

function renderConditionOnsetDateAction(
  entry: Extract<ConditionActivityEvent, { kind: 'changed-onset-date' }>
): ReactNode {
  if (!entry.currentOnsetDate) {
    return 'removed onset date'
  }

  if (!entry.previousOnsetDate) {
    return (
      <>
        set onset date to{' '}
        <ActivityValue>
          {formatDateNumber(entry.currentOnsetDate)}
        </ActivityValue>
      </>
    )
  }

  return (
    <>
      changed onset date from{' '}
      <ActivityValue>{formatDateNumber(entry.previousOnsetDate)}</ActivityValue>{' '}
      to{' '}
      <ActivityValue>{formatDateNumber(entry.currentOnsetDate)}</ActivityValue>
    </>
  )
}

function ConditionActivityConditionValue({
  name,
  icd10Codes,
}: {
  name: string
  icd10Codes: string[]
}): JSX.Element {
  return (
    <span className="font-medium text-foreground">
      {name}
      {icd10Codes.length > 0 && (
        <>
          {' '}
          <ConditionActivityCodeBadges codes={icd10Codes} />
        </>
      )}
    </span>
  )
}

function ConditionActivityCodeBadges({
  codes,
}: {
  codes: string[]
}): JSX.Element | null {
  if (codes.length === 0) {
    return null
  }

  return (
    <span className="inline-flex items-center gap-1 align-middle">
      {codes.map((code) => (
        <Badge key={code} variant="code">
          {code}
        </Badge>
      ))}
    </span>
  )
}

function ActivityValue({ children }: { children: ReactNode }): JSX.Element {
  return <span className="font-medium text-foreground">{children}</span>
}

function assertNever(value: never): never {
  throw new Error(`Unhandled condition activity event: ${value}`)
}
